import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useCallback, useState } from 'react';
import { postSharedBasket, postSharedBasketDisable } from '@/lib/api';
import {
  ApiError,
  SharedBasketCreateRequest,
  SharedBasketCreateResponse,
} from '@/lib/types';
import { sharedBasketQueryKeys } from '@/lib/sharedBasketQueryKeys';

// The 403 names the offending file in details[0].value; everything else gets
// the server's own message, which is more use than a generic string.
const readCreateError = (error: AxiosError<ApiError>) => {
  const status = error.response?.status;
  const details = error.response?.data?.error?.details;
  const message = error.response?.data?.error?.message;

  if (status === 403) {
    const fileId = details?.[0]?.value;
    return fileId
      ? `You don't have access to file ${fileId}, so it can't be shared. Remove it from the basket and try again.`
      : "You don't have access to one of these files, so it can't be shared.";
  }
  if (status === 422) {
    return message || 'Those recipients or that expiry are not valid.';
  }
  return message || 'Could not create the share link. Please try again.';
};

// Disable is irreversible and access-revoking: a silent failure would leave
// the user believing a live link is dead.
const readDisableError = (error: AxiosError<ApiError>) => {
  const status = error.response?.status;
  const message = error.response?.data?.error?.message;

  if (status === 403) {
    return 'You do not have permission to disable this link.';
  }
  return (
    message || 'Could not disable the link. It is still active.'
  );
};

export const useShareBasket = () => {
  const queryClient = useQueryClient();
  const [basket, setBasket] =
    useState<SharedBasketCreateResponse | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [disableError, setDisableError] = useState<string | null>(
    null,
  );

  const {
    mutate: mutateCreate,
    isPending: isCreating,
    reset: resetCreate,
  } = useMutation<
    SharedBasketCreateResponse,
    AxiosError<ApiError>,
    SharedBasketCreateRequest
  >({
    mutationFn: postSharedBasket,
    onSuccess: (response) => {
      setBasket(response);
      setCreateError(null);
      queryClient.invalidateQueries({
        queryKey: sharedBasketQueryKeys.mine,
      });
    },
    onError: (error) => setCreateError(readCreateError(error)),
  });

  const { mutate: mutateDisable, isPending: isDisabling } =
    useMutation<void, AxiosError<ApiError>, string>({
      mutationFn: postSharedBasketDisable,
      onError: (error) => setDisableError(readDisableError(error)),
      onSuccess: (_data, token) => {
        setDisableError(null);
        queryClient.invalidateQueries({
          queryKey: sharedBasketQueryKeys.byToken(token),
        });
        queryClient.invalidateQueries({
          queryKey: sharedBasketQueryKeys.mine,
        });
        setBasket((previous) =>
          previous && previous.token === token
            ? { ...previous, status: 'disabled', can_disable: false }
            : previous,
        );
      },
    });

  const create = useCallback(
    (request: SharedBasketCreateRequest) => {
      setCreateError(null);
      mutateCreate(request);
    },
    [mutateCreate],
  );

  const disable = useCallback(
    (token: string) => {
      setDisableError(null);
      mutateDisable(token);
    },
    [mutateDisable],
  );

  const clearDisableError = useCallback(
    () => setDisableError(null),
    [],
  );

  const reset = useCallback(() => {
    setBasket(null);
    setCreateError(null);
    setDisableError(null);
    resetCreate();
  }, [resetCreate]);

  return {
    basket,
    create,
    isCreating,
    createError,
    disable,
    isDisabling,
    disableError,
    clearDisableError,
    reset,
  };
};

export default useShareBasket;
